/** Shared upload transport contract. Same source in Admin and Photographer CRA apps. */
export const PHOTO_MIME_TYPES = ["image/jpeg", "image/png", "image/heic", "image/heif"];
export const MAX_BULK_FILES = 500;
export function photoMimeType(file) {
  if (PHOTO_MIME_TYPES.includes(file.type)) return file.type;
  const suffix = String(file.name || "").split(".").pop().toLowerCase();
  return { jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", heic: "image/heic", heif: "image/heif" }[suffix] || "";
}

export function validateBulkFiles(files) {
  const items = Array.from(files || []);
  if (!items.length) throw new Error("Choose at least one photograph.");
  if (items.length > MAX_BULK_FILES) throw new Error(`Select no more than ${MAX_BULK_FILES} photos at a time.`);
  for (const file of items) {
    if (!photoMimeType(file)) throw new Error(`${file.name}: unsupported image format.`);
    if (!file.size) throw new Error(`${file.name}: the file is empty.`);
  }
  return items;
}

export function mapBulkContracts(files, response) {
  const session = response.session;
  const contracts = new Map((response.contracts || []).map((contract) => [contract.photo?.id, contract]));
  if (!session || !Array.isArray(session.items) || session.items.length !== files.length) {
    throw new Error("Unexpected bulk-upload session response; no files were transferred.");
  }
  return session.items.map((item, index) => ({
    file: files[index],
    photoId: item.photoId,
    contract: item.photoId ? contracts.get(item.photoId) : null,
    error: item.error?.message
  }));
}

/** Apply terminal backend processing results to the matching local upload rows. */
export function reconcileBulkEntries(entries, job) {
  const items = new Map((job?.items || []).filter((item) => item.photoId).map((item) => [item.photoId, item]));
  return entries.map((entry) => {
    const item = items.get(entry.photoId);
    if (!item || entry.status === "uploading") return entry;
    if (item.status === "ready") return { ...entry, status: "ready", progress: 100, error: "" };
    if (item.status === "failed") return {
      ...entry,
      status: "failed",
      progress: 0,
      error: item.failure?.message || item.error?.message || "Image processing failed"
    };
    return entry;
  });
}

export class StorageUploadError extends Error {
  constructor(message, code, status = 0) {
    super(message);
    this.name = "StorageUploadError";
    this.code = code;
    this.status = status;
  }
}

export function isStorageAccessFailure(error) {
  return ["STORAGE_NETWORK_OR_CORS", "STORAGE_FORBIDDEN", "STORAGE_ETAG_NOT_EXPOSED"].includes(error?.code);
}

export function putSignedBlob(url, { method = "PUT", headers = {}, body, signal, onProgress } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    // Large photo transfers may take several minutes on mobile connections.
    xhr.timeout = 10 * 60 * 1000;
    Object.entries(headers).forEach(([key, value]) => xhr.setRequestHeader(key, value));
    xhr.upload.onprogress = (event) => { if (event.lengthComputable) onProgress?.(event.loaded, event.total); };
    const abort = () => xhr.abort();
    if (signal?.aborted) { reject(new DOMException("Upload cancelled", "AbortError")); return; }
    signal?.addEventListener("abort", abort, { once: true });
    function complete() { signal?.removeEventListener("abort", abort); }
    xhr.onload = () => {
      complete();
      if (xhr.status >= 200 && xhr.status < 300) resolve({ etag: xhr.getResponseHeader("ETag") });
      else if (xhr.status === 403) reject(new StorageUploadError(
        "Storage refused this upload (HTTP 403). Check the signed URL expiry, object-storage credentials and bucket permissions.",
        "STORAGE_FORBIDDEN", 403
      ));
      else reject(new StorageUploadError(`Storage upload failed (HTTP ${xhr.status}).`, "STORAGE_HTTP_ERROR", xhr.status));
    };
    xhr.onerror = () => {
      complete();
      reject(new StorageUploadError(
        "Storage connection was blocked or interrupted. Check your network and the R2 bucket CORS policy for this website's origin, PUT and Content-Type.",
        "STORAGE_NETWORK_OR_CORS"
      ));
    };
    xhr.ontimeout = () => { complete(); reject(new StorageUploadError("Upload timed out. Retry this file on a stable connection.", "STORAGE_TIMEOUT")); };
    xhr.onabort = () => { complete(); reject(new DOMException("Upload cancelled", "AbortError")); };
    try { xhr.send(body); } catch (error) { complete(); reject(error); }
  });
}

export async function uploadSignedContract({ eventId, file, contract, api, signal, onProgress }) {
  const { photo, upload } = contract || {};
  if (!photo?.id || !upload?.mode) throw new Error("Upload contract is missing a photo or mode.");
  if (upload.mode === "complete") return photo;
  try {
    let parts;
    if (upload.mode === "multipart") {
      parts = [];
      let done = 0;
      for (let partNumber = 1; partNumber <= upload.partCount; partNumber++) {
        if (signal?.aborted) throw new DOMException("Upload cancelled", "AbortError");
        const begin = (partNumber - 1) * upload.partSizeBytes;
        const blob = file.slice(begin, Math.min(file.size, begin + upload.partSizeBytes));
        const signed = await api.signPart(eventId, photo.id, partNumber, signal);
        const result = await putSignedBlob(signed.url, {
          method: signed.method || "PUT", headers: signed.headers || {}, body: blob, signal,
          onProgress: (loaded) => onProgress?.(done + loaded, file.size)
        });
        // Do not fabricate ETags: the storage CORS policy must expose ETag.
        if (!result.etag) throw new StorageUploadError(
          "Multipart upload requires ETag in the R2 bucket's CORS ExposeHeaders list.",
          "STORAGE_ETAG_NOT_EXPOSED"
        );
        parts.push({ partNumber, etag: result.etag });
        done += blob.size;
      }
    } else if (upload.mode === "single") {
      await putSignedBlob(upload.url, {
        method: upload.method || "PUT", headers: upload.headers || {}, body: file, signal, onProgress
      });
    } else throw new Error(`Unsupported upload mode: ${upload.mode}`);
    await api.complete(eventId, photo.id, parts ? { parts } : {}, signal);
    return photo;
  } catch (error) {
    if (error.name === "AbortError") {
      await api.abort(eventId, photo.id).catch(() => undefined);
    }
    throw error;
  }
}

export async function uploadWithConcurrency(items, worker, concurrency = 3, { shouldStop = () => false } = {}) {
  let cursor = 0;
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (cursor < items.length && !shouldStop()) {
      const index = cursor++;
      try { await worker(items[index], index); }
      catch { /* The caller records the individual file's failure. */ }
    }
  }));
  return { scheduled: cursor, skipped: items.length - cursor };
}

export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Retry when the event media-admission lease is busy (parallel initiate races). */
export async function withAdmissionRetry(operation, { attempts = 6, baseDelayMs = 75 } = {}) {
  let lastError;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (error?.code !== "UPLOAD_CAPACITY_BUSY" || attempt === attempts - 1) throw error;
      await sleep(baseDelayMs * 2 ** attempt + Math.floor(Math.random() * baseDelayMs));
    }
  }
  throw lastError;
}
